import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  ScrollView,
  RefreshControl,
  StatusBar,
  Alert,
  Modal,
  FlatList,
  ActivityIndicator,
  TextInput,
} from 'react-native';
import {
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ===== API Configuration =====
const API_BASE_URL = 'https://api.jobeng.ai';

const getCompanyId = async () => {
  try {
    const userData = await AsyncStorage.getItem('currentUser');
    if (userData) {
      const user = JSON.parse(userData);
      return user.company_id || user.companyId || null;
    }
    const companyProfile = await AsyncStorage.getItem('companyProfile');
    if (companyProfile) {
      const company = JSON.parse(companyProfile);
      return company.id || company.company_id || null;
    }
    return null;
  } catch (e) {
    console.error('Error getting company_id:', e);
    return null;
  }
};

const apiRequest = async (endpoint, options = {}) => {
  try {
    const companyId = await getCompanyId();
    // Split endpoint and query string
    const [path, queryString] = endpoint.split('?');
    // Route: auth/* goes to /api/auth/*, everything else to /api/data/{table}
    let baseUrl;
    if (path.startsWith('auth/')) {
      baseUrl = `${API_BASE_URL}/api/${path}`;
    } else {
      // Convert hyphens to underscores for DB table mapping
      const dbPath = path.replace(/-/g, '_');
      baseUrl = `${API_BASE_URL}/api/data/${dbPath}`;
    }
    const params = new URLSearchParams(queryString || '');
    if (companyId) params.set('company_id', companyId);
    const separator = params.toString() ? '?' : '';
    const url = `${baseUrl}${separator}${params.toString()}`;

    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      ...options,
      ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    });

    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};
// ===== End API Configuration =====


const { width, height } = Dimensions.get('window');

export default function QuoteGeneratorScreen({ navigation }) {
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('quotes');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Quote form data
  const [quoteForm, setQuoteForm] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerCompany: '',
    projectTitle: '',
    projectDescription: '',
    validUntil: '',
    terms: 'Net 30',
    notes: '',
    items: []
  });

  // Existing quotes
  const [quotes, setQuotes] = useState([
    {
      id: 'QUO-001',
      customerName: 'John Smith',
      customerEmail: 'john.smith@techcorp.com',
      customerCompany: 'TechCorp Ltd',
      projectTitle: 'Enterprise Software Implementation',
      totalAmount: 125000.00,
      status: 'sent',
      createdDate: '2024-01-15T10:30:00Z',
      validUntil: '2024-02-15T23:59:59Z',
      sentDate: '2024-01-15T14:20:00Z',
      items: [
        { id: '1', description: 'Software License (Enterprise)', quantity: 1, unitPrice: 85000.00, total: 85000.00 },
        { id: '2', description: 'Implementation Services', quantity: 200, unitPrice: 150.00, total: 30000.00 },
        { id: '3', description: 'Training Program', quantity: 20, unitPrice: 500.00, total: 10000.00 }
      ],
      subtotal: 125000.00,
      tax: 25000.00,
      total: 150000.00,
      notes: 'Includes 1-year support and maintenance',
      terms: 'Net 30'
    },
    {
      id: 'QUO-002',
      customerName: 'Sarah Johnson',
      customerEmail: 'sarah.j@greenbuilders.com',
      customerCompany: 'Green Builders Ltd',
      projectTitle: 'Construction Management System',
      totalAmount: 75000.00,
      status: 'draft',
      createdDate: '2024-01-18T09:15:00Z',
      validUntil: '2024-02-18T23:59:59Z',
      sentDate: null,
      items: [
        { id: '1', description: 'Project Management Software', quantity: 1, unitPrice: 45000.00, total: 45000.00 },
        { id: '2', description: 'Custom Development', quantity: 100, unitPrice: 200.00, total: 20000.00 },
        { id: '3', description: 'Data Migration', quantity: 1, unitPrice: 10000.00, total: 10000.00 }
      ],
      subtotal: 75000.00,
      tax: 15000.00,
      total: 90000.00,
      notes: 'Includes 6-month warranty',
      terms: 'Net 30'
    },
    {
      id: 'QUO-003',
      customerName: 'Mike Chen',
      customerEmail: 'mike.chen@urbandev.com',
      customerCompany: 'Urban Development Co',
      projectTitle: 'Office Renovation Project',
      totalAmount: 185000.00,
      status: 'accepted',
      createdDate: '2024-01-10T11:45:00Z',
      validUntil: '2024-02-10T23:59:59Z',
      sentDate: '2024-01-10T16:30:00Z',
      acceptedDate: '2024-01-12T14:20:00Z',
      items: [
        { id: '1', description: 'Design Consultation', quantity: 40, unitPrice: 250.00, total: 10000.00 },
        { id: '2', description: 'Construction Materials', quantity: 1, unitPrice: 120000.00, total: 120000.00 },
        { id: '3', description: 'Labor and Installation', quantity: 300, unitPrice: 150.00, total: 45000.00 },
        { id: '4', description: 'Project Management', quantity: 1, unitPrice: 10000.00, total: 10000.00 }
      ],
      subtotal: 185000.00,
      tax: 37000.00,
      total: 222000.00,
      notes: 'Premium materials and finishes included',
      terms: 'Net 30'
    },
    {
      id: 'QUO-004',
      customerName: 'Emma Wilson',
      customerEmail: 'emma.w@buildfast.com',
      customerCompany: 'BuildFast Construction',
      projectTitle: 'Equipment Rental Package',
      totalAmount: 45000.00,
      status: 'expired',
      createdDate: '2024-01-05T08:20:00Z',
      validUntil: '2024-01-20T23:59:59Z',
      sentDate: '2024-01-05T12:15:00Z',
      items: [
        { id: '1', description: 'Excavator Rental (6 months)', quantity: 1, unitPrice: 25000.00, total: 25000.00 },
        { id: '2', description: 'Crane Rental (3 months)', quantity: 1, unitPrice: 15000.00, total: 15000.00 },
        { id: '3', description: 'Delivery and Setup', quantity: 1, unitPrice: 5000.00, total: 5000.00 }
      ],
      subtotal: 45000.00,
      tax: 9000.00,
      total: 54000.00,
      notes: 'Includes maintenance and insurance',
      terms: 'Net 15'
    },
    {
      id: 'QUO-005',
      customerName: 'David Brown',
      customerEmail: 'david.brown@constructplus.com',
      customerCompany: 'Construct Plus',
      projectTitle: 'Safety Training Program',
      totalAmount: 28000.00,
      status: 'rejected',
      createdDate: '2024-01-08T13:30:00Z',
      validUntil: '2024-02-08T23:59:59Z',
      sentDate: '2024-01-08T15:45:00Z',
      rejectedDate: '2024-01-14T10:20:00Z',
      items: [
        { id: '1', description: 'Safety Training Course', quantity: 50, unitPrice: 400.00, total: 20000.00 },
        { id: '2', description: 'Training Materials', quantity: 50, unitPrice: 80.00, total: 4000.00 },
        { id: '3', description: 'Certification Processing', quantity: 50, unitPrice: 80.00, total: 4000.00 }
      ],
      subtotal: 28000.00,
      tax: 5600.00,
      total: 33600.00,
      notes: 'OSHA compliant training program',
      terms: 'Net 30'
    }
  ]);

  // Quote templates
  const [quoteTemplates, setQuoteTemplates] = useState([
    {
      id: '1',
      name: 'Software Implementation',
      description: 'Standard template for software projects',
      category: 'Technology',
      items: [
        { description: 'Software License', quantity: 1, unitPrice: 50000.00 },
        { description: 'Implementation Services', quantity: 100, unitPrice: 150.00 },
        { description: 'Training Program', quantity: 10, unitPrice: 500.00 }
      ],
      terms: 'Net 30',
      validityDays: 30
    },
    {
      id: '2',
      name: 'Construction Project',
      description: 'Template for construction and renovation',
      category: 'Construction',
      items: [
        { description: 'Design Consultation', quantity: 20, unitPrice: 250.00 },
        { description: 'Materials', quantity: 1, unitPrice: 75000.00 },
        { description: 'Labor', quantity: 200, unitPrice: 150.00 }
      ],
      terms: 'Net 30',
      validityDays: 45
    },
    {
      id: '3',
      name: 'Training Services',
      description: 'Professional training and certification',
      category: 'Education',
      items: [
        { description: 'Training Course', quantity: 25, unitPrice: 400.00 },
        { description: 'Materials', quantity: 25, unitPrice: 80.00 },
        { description: 'Certification', quantity: 25, unitPrice: 80.00 }
      ],
      terms: 'Net 30',
      validityDays: 30
    },
    {
      id: '4',
      name: 'Equipment Rental',
      description: 'Equipment and machinery rental',
      category: 'Equipment',
      items: [
        { description: 'Equipment Rental', quantity: 1, unitPrice: 20000.00 },
        { description: 'Delivery', quantity: 1, unitPrice: 2000.00 },
        { description: 'Maintenance', quantity: 1, unitPrice: 3000.00 }
      ],
      terms: 'Net 15',
      validityDays: 15
    }
  ]);

  // Quote statistics
  const [quoteStats, setQuoteStats] = useState({
    totalQuotes: 5,
    draftQuotes: 1,
    sentQuotes: 1,
    acceptedQuotes: 1,
    rejectedQuotes: 1,
    expiredQuotes: 1,
    totalValue: 458000.00,
    acceptedValue: 185000.00,
    conversionRate: 20.0,
    avgQuoteValue: 91600.00
  });

  useEffect(() => {
    initializeScreen();
  }, []);

  const initializeScreen = async () => {
    try {
      setIsLoading(true);
      // Load quotes from API
      try {
        const [quotesResp, templatesResp, statsResp] = await Promise.all([
          apiRequest('quotes'),
          apiRequest('quote-templates'),
          apiRequest('quote-stats'),
        ]);
        if (quotesResp.success && quotesResp.data) setQuotes(quotesResp.data);
        if (templatesResp.success && templatesResp.data) setQuoteTemplates(templatesResp.data);
        if (statsResp.success && statsResp.data) setQuoteStats(statsResp.data);
      } catch (apiError) {
        console.warn('API unavailable, using local data:', apiError.message);
      }
    } catch (error) {
      console.error('Error initializing screen:', error);
      Alert.alert('Error', 'Failed to load quote generator data');
    } finally {
      setIsLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setRefreshing(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'draft': '#6B7280',
      'sent': '#3B82F6',
      'accepted': '#10B981',
      'rejected': '#EF4444',
      'expired': '#F59E0B'
    };
    return colors[status] || '#6B7280';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'draft': 'file-edit',
      'sent': 'send',
      'accepted': 'check-circle',
      'rejected': 'close-circle',
      'expired': 'clock-alert'
    };
    return icons[status] || 'file';
  };

  const filteredQuotes = quotes.filter(quote =>
    String(quote.customerName ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    String(quote.customerCompany ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    String(quote.projectTitle ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    String(quote.id ?? '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreateQuote = () => {
    setQuoteForm({
      customerName: '',
      customerEmail: '',
      customerPhone: '',
      customerCompany: '',
      projectTitle: '',
      projectDescription: '',
      validUntil: '',
      terms: 'Net 30',
      notes: '',
      items: []
    });
    setShowCreateModal(true);
  };

  const handleSaveQuote = () => {
    if (!quoteForm.customerName || !quoteForm.projectTitle || quoteForm.items.length === 0) {
      Alert.alert('Error', 'Please fill in all required fields and add at least one item');
      return;
    }

    const newQuote = {
      id: `QUO-${String(quotes.length + 1).padStart(3, '0')}`,
      ...quoteForm,
      totalAmount: quoteForm.items.reduce((sum, item) => sum + item.total, 0),
      status: 'draft',
      createdDate: new Date().toISOString(),
      sentDate: null,
      subtotal: quoteForm.items.reduce((sum, item) => sum + item.total, 0),
      tax: quoteForm.items.reduce((sum, item) => sum + item.total, 0) * 0.2,
      total: quoteForm.items.reduce((sum, item) => sum + item.total, 0) * 1.2
    };

    setQuotes([newQuote, ...quotes]);
    setShowCreateModal(false);
    Alert.alert('Success', 'Quote created successfully');
  };

  const handleSendQuote = (quoteId) => {
    setQuotes(quotes.map(quote => 
      quote.id === quoteId 
        ? { ...quote, status: 'sent', sentDate: new Date().toISOString() }
        : quote
    ));
    Alert.alert('Success', 'Quote sent successfully');
  };

  const handleDuplicateQuote = (quote) => {
    const duplicatedQuote = {
      ...quote,
      id: `QUO-${String(quotes.length + 1).padStart(3, '0')}`,
      status: 'draft',
      createdDate: new Date().toISOString(),
      sentDate: null,
      acceptedDate: null,
      rejectedDate: null
    };
    setQuotes([duplicatedQuote, ...quotes]);
    Alert.alert('Success', 'Quote duplicated successfully');
  };

  const addQuoteItem = () => {
    const newItem = {
      id: String(quoteForm.items.length + 1),
      description: '',
      quantity: 1,
      unitPrice: 0,
      total: 0
    };
    setQuoteForm({
      ...quoteForm,
      items: [...quoteForm.items, newItem]
    });
  };

  const updateQuoteItem = (itemId, field, value) => {
    const updatedItems = quoteForm.items.map(item => {
      if (item.id === itemId) {
        const updatedItem = { ...item, [field]: value };
        if (field === 'quantity' || field === 'unitPrice') {
          updatedItem.total = updatedItem.quantity * updatedItem.unitPrice;
        }
        return updatedItem;
      }
      return item;
    });
    setQuoteForm({ ...quoteForm, items: updatedItems });
  };

  const removeQuoteItem = (itemId) => {
    setQuoteForm({
      ...quoteForm,
      items: quoteForm.items.filter(item => item.id !== itemId)
    });
  };

  const renderHeader = () => (
    <LinearGradient
      colors={['#1F2937', '#374151']}
      style={styles.header}
    >
      <View style={styles.headerContent}>
        <TouchableOpacity 
          onPress={() => navigation?.goBack()}
          style={styles.backButton}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color="#FFFFFF" />
        </TouchableOpacity>
        
        <View style={styles.headerCenter}>
          <Text style={styles.headerTitle}>Quote Generator</Text>
          <Text style={styles.headerSubtitle}>Create and Manage Quotes</Text>
        </View>
        
        <TouchableOpacity 
          style={styles.headerButton}
          onPress={handleCreateQuote}
        >
          <MaterialCommunityIcons name="plus" size={24} color="#FFFFFF" />
        </TouchableOpacity>
      </View>
    </LinearGradient>
  );

  const renderTabs = () => (
    <View style={styles.tabsContainer}>
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.tabsContent}
      >
        {[
          { key: 'quotes', title: 'Quotes', icon: 'file-document' },
          { key: 'templates', title: 'Templates', icon: 'file-multiple' },
          { key: 'analytics', title: 'Analytics', icon: 'chart-line' }
        ].map((tab) => (
          <TouchableOpacity
            key={tab.key}
            style={[
              styles.tab,
              activeTab === tab.key && styles.activeTab
            ]}
            onPress={() => setActiveTab(tab.key)}
          >
            <MaterialCommunityIcons 
              name={tab.icon} 
              size={20} 
              color={activeTab === tab.key ? '#3B82F6' : '#6B7280'} 
            />
            <Text style={[
              styles.tabText,
              activeTab === tab.key && styles.activeTabText
            ]}>
              {tab.title}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderSearchBar = () => (
    <View style={styles.searchContainer}>
      <View style={styles.searchBar}>
        <MaterialCommunityIcons name="magnify" size={20} color="#6B7280" />
        <TextInput
          style={styles.searchInput}
          placeholder="Search quotes..."
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholderTextColor="#9CA3AF"
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <MaterialCommunityIcons name="close" size={20} color="#6B7280" />
          </TouchableOpacity>
        )}
      </View>
    </View>
  );

  const renderQuoteStats = () => {
    const stats = [
      {
        title: 'Total Quotes',
        value: quoteStats.totalQuotes.toString(),
        icon: 'file-document-multiple',
        color: '#3B82F6'
      },
      {
        title: 'Accepted',
        value: quoteStats.acceptedQuotes.toString(),
        icon: 'check-circle',
        color: '#10B981'
      },
      {
        title: 'Total Value',
        value: formatCurrency(quoteStats.totalValue),
        icon: 'currency-usd',
        color: '#F59E0B'
      },
      {
        title: 'Conversion Rate',
        value: `${quoteStats.conversionRate}%`,
        icon: 'percent',
        color: '#8B5CF6'
      }
    ];

  
  // Save quote via API
  const saveQuoteToApi = async (quote) => {
    try {
      await apiRequest('quotes', {
        method: quote.id ? 'PUT' : 'POST',
        body: quote,
      });
    } catch (error) {
      console.warn('API save failed:', error.message);
    }
  };

  return (
      <FlatList
        data={stats}
        keyExtractor={(item, index) => index.toString()}
        renderItem={({ item }) => (
          <View style={styles.statCard}>
            <View style={[styles.statIcon, { backgroundColor: item.color }]}>
              <MaterialCommunityIcons name={item.icon} size={24} color="#FFFFFF" />
            </View>
            <Text style={styles.statValue}>{item.value}</Text>
            <Text style={styles.statTitle}>{item.title}</Text>
          </View>
        )}
        numColumns={2}
        columnWrapperStyle={styles.row}
        contentContainerStyle={styles.statsContainer}
        scrollEnabled={false}
      />
    );
  };

  const renderQuotesList = () => (
    <FlatList
      data={filteredQuotes}
      keyExtractor={(item) => item.id}
      renderItem={({ item }) => (
        <TouchableOpacity 
          style={styles.quoteCard}
          onPress={() => {
            setSelectedQuote(item);
            setShowQuoteModal(true);
          }}
        >
          <View style={styles.quoteHeader}>
            <View style={styles.quoteInfo}>
              <Text style={styles.quoteId}>{item.id}</Text>
              <Text style={styles.quoteTitle}>{item.projectTitle}</Text>
              <Text style={styles.quoteCustomer}>{item.customerName} - {item.customerCompany}</Text>
            </View>
            <View style={styles.quoteStatus}>
              <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
                <MaterialCommunityIcons 
                  name={getStatusIcon(item.status)} 
                  size={12} 
                  color="#FFFFFF" 
                />
                <Text style={styles.statusText}>{item.status}</Text>
              </View>
            </View>
          </View>
          
          <View style={styles.quoteDetails}>
            <View style={styles.quoteDetail}>
              <Text style={styles.quoteDetailLabel}>Amount</Text>
              <Text style={styles.quoteDetailValue}>{formatCurrency(item.totalAmount)}</Text>
            </View>
            <View style={styles.quoteDetail}>
              <Text style={styles.quoteDetailLabel}>Created</Text>
              <Text style={styles.quoteDetailValue}>{formatDate(item.createdDate)}</Text>
            </View>
            <View style={styles.quoteDetail}>
              <Text style={styles.quoteDetailLabel}>Valid Until</Text>
              <Text style={styles.quoteDetailValue}>{formatDate(item.validUntil)}</Text>
            </View>
          </View>
          
          <View style={styles.quoteActions}>
            {item.status === 'draft' && (
              <TouchableOpacity 
                style={[styles.actionButton, { backgroundColor: '#3B82F6' }]}
                onPress={() => handleSendQuote(item.id)}
              >
                <MaterialCommunityIcons name="send" size={16} color="#FFFFFF" />
                <Text style={styles.actionButtonText}>Send</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity 
              style={[styles.actionButton, { backgroundColor: '#6B7280' }]}
              onPress={() => handleDuplicateQuote(item)}
            >
              <MaterialCommunityIcons name="content-copy" size={16} color="#FFFFFF" />
              <Text style={styles.actionButtonText}>Duplicate</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      )}
      contentContainerStyle={styles.listContainer}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          colors={['#3B82F6']}
        />
      }
      showsVerticalScrollIndicator={false}
    />
  );

  const renderTemplates = () => (
    <FlatList
      data={quoteTemplates}
      keyExtractor={(item) => item.id}
      renderItem={({ item }) => (
        <TouchableOpacity 
          style={styles.templateCard}
          onPress={() => {
            setQuoteForm({
              ...quoteForm,
              items: item.items.map((templateItem, index) => ({
                id: String(index + 1),
                ...templateItem,
                total: templateItem.quantity * templateItem.unitPrice
              })),
              terms: item.terms,
              validUntil: new Date(Date.now() + item.validityDays * 24 * 60 * 60 * 1000).toISOString()
            });
            setShowCreateModal(true);
          }}
        >
          <View style={styles.templateHeader}>
            <Text style={styles.templateName}>{item.name}</Text>
            <View style={styles.templateCategory}>
              <Text style={styles.templateCategoryText}>{item.category}</Text>
            </View>
          </View>
          <Text style={styles.templateDescription}>{item.description}</Text>
          <View style={styles.templateDetails}>
            <Text style={styles.templateDetail}>Items: {item.items.length}</Text>
            <Text style={styles.templateDetail}>Terms: {item.terms}</Text>
            <Text style={styles.templateDetail}>Validity: {item.validityDays} days</Text>
          </View>
        </TouchableOpacity>
      )}
      numColumns={2}
      columnWrapperStyle={styles.row}
      contentContainerStyle={styles.listContainer}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          colors={['#3B82F6']}
        />
      }
      showsVerticalScrollIndicator={false}
    />
  );

  const renderAnalytics = () => (
    <ScrollView 
      style={styles.contentContainer}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          colors={['#3B82F6']}
        />
      }
      showsVerticalScrollIndicator={false}
    >
      {renderQuoteStats()}
      
      <View style={styles.analyticsSection}>
        <Text style={styles.sectionTitle}>Quote Status Distribution</Text>
        <View style={styles.statusDistribution}>
          {[
            { status: 'draft', count: quoteStats.draftQuotes, color: '#6B7280' },
            { status: 'sent', count: quoteStats.sentQuotes, color: '#3B82F6' },
            { status: 'accepted', count: quoteStats.acceptedQuotes, color: '#10B981' },
            { status: 'rejected', count: quoteStats.rejectedQuotes, color: '#EF4444' },
            { status: 'expired', count: quoteStats.expiredQuotes, color: '#F59E0B' }
          ].map((item, index) => (
            <View key={index} style={styles.statusItem}>
              <View style={[styles.statusIndicator, { backgroundColor: item.color }]} />
              <Text style={styles.statusLabel}>{item.status}</Text>
              <Text style={styles.statusCount}>{item.count}</Text>
            </View>
          ))}
        </View>
      </View>
    </ScrollView>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'quotes':
        return (
          <View style={styles.contentContainer}>
            {renderSearchBar()}
            {renderQuotesList()}
          </View>
        );
      case 'templates':
        return renderTemplates();
      case 'analytics':
        return renderAnalytics();
      default:
        return null;
    }
  };

  const renderCreateModal = () => (
    <Modal
      visible={showCreateModal}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => setShowCreateModal(false)}
    >
      <View style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>Create New Quote</Text>
          <TouchableOpacity 
            style={styles.modalCloseButton}
            onPress={() => setShowCreateModal(false)}
          >
            <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
          </TouchableOpacity>
        </View>
        
        <ScrollView style={styles.modalContent}>
          <View style={styles.formSection}>
            <Text style={styles.formSectionTitle}>Customer Information</Text>
            <TextInput
              style={styles.formInput}
              placeholder="Customer Name *"
              value={quoteForm.customerName}
              onChangeText={(text) => setQuoteForm({ ...quoteForm, customerName: text })}
            />
            <TextInput
              style={styles.formInput}
              placeholder="Customer Email"
              value={quoteForm.customerEmail}
              onChangeText={(text) => setQuoteForm({ ...quoteForm, customerEmail: text })}
              keyboardType="email-address"
            />
            <TextInput
              style={styles.formInput}
              placeholder="Customer Phone"
              value={quoteForm.customerPhone}
              onChangeText={(text) => setQuoteForm({ ...quoteForm, customerPhone: text })}
              keyboardType="phone-pad"
            />
            <TextInput
              style={styles.formInput}
              placeholder="Company Name"
              value={quoteForm.customerCompany}
              onChangeText={(text) => setQuoteForm({ ...quoteForm, customerCompany: text })}
            />
          </View>
          
          <View style={styles.formSection}>
            <Text style={styles.formSectionTitle}>Project Information</Text>
            <TextInput
              style={styles.formInput}
              placeholder="Project Title *"
              value={quoteForm.projectTitle}
              onChangeText={(text) => setQuoteForm({ ...quoteForm, projectTitle: text })}
            />
            <TextInput
              style={[styles.formInput, styles.textArea]}
              placeholder="Project Description"
              value={quoteForm.projectDescription}
              onChangeText={(text) => setQuoteForm({ ...quoteForm, projectDescription: text })}
              multiline
              numberOfLines={3}
            />
          </View>
          
          <View style={styles.formSection}>
            <View style={styles.formSectionHeader}>
              <Text style={styles.formSectionTitle}>Quote Items</Text>
              <TouchableOpacity 
                style={styles.addItemButton}
                onPress={addQuoteItem}
              >
                <MaterialCommunityIcons name="plus" size={20} color="#3B82F6" />
                <Text style={styles.addItemText}>Add Item</Text>
              </TouchableOpacity>
            </View>
            
            {quoteForm.items.map((item, index) => (
              <View key={item.id} style={styles.quoteItemForm}>
                <View style={styles.quoteItemHeader}>
                  <Text style={styles.quoteItemTitle}>Item {index + 1}</Text>
                  <TouchableOpacity 
                    onPress={() => removeQuoteItem(item.id)}
                    style={styles.removeItemButton}
                  >
                    <MaterialCommunityIcons name="close" size={16} color="#EF4444" />
                  </TouchableOpacity>
                </View>
                <TextInput
                  style={styles.formInput}
                  placeholder="Description"
                  value={item.description}
                  onChangeText={(text) => updateQuoteItem(item.id, 'description', text)}
                />
                <View style={styles.quoteItemRow}>
                  <TextInput
                    style={[styles.formInput, styles.quantityInput]}
                    placeholder="Qty"
                    value={item.quantity.toString()}
                    onChangeText={(text) => updateQuoteItem(item.id, 'quantity', parseFloat(text) || 0)}
                    keyboardType="numeric"
                  />
                  <TextInput
                    style={[styles.formInput, styles.priceInput]}
                    placeholder="Unit Price"
                    value={item.unitPrice.toString()}
                    onChangeText={(text) => updateQuoteItem(item.id, 'unitPrice', parseFloat(text) || 0)}
                    keyboardType="numeric"
                  />
                  <Text style={styles.totalText}>{formatCurrency(item.total)}</Text>
                </View>
              </View>
            ))}
          </View>
          
          <View style={styles.formSection}>
            <Text style={styles.formSectionTitle}>Terms & Notes</Text>
            <TextInput
              style={styles.formInput}
              placeholder="Payment Terms"
              value={quoteForm.terms}
              onChangeText={(text) => setQuoteForm({ ...quoteForm, terms: text })}
            />
            <TextInput
              style={[styles.formInput, styles.textArea]}
              placeholder="Additional Notes"
              value={quoteForm.notes}
              onChangeText={(text) => setQuoteForm({ ...quoteForm, notes: text })}
              multiline
              numberOfLines={3}
            />
          </View>
        </ScrollView>
        
        <View style={styles.modalFooter}>
          <TouchableOpacity 
            style={[styles.modalButton, styles.cancelButton]}
            onPress={() => setShowCreateModal(false)}
          >
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={[styles.modalButton, styles.saveButton]}
            onPress={handleSaveQuote}
          >
            <Text style={styles.saveButtonText}>Save Quote</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  const renderQuoteModal = () => (
    <Modal
      visible={showQuoteModal}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => {
        setShowQuoteModal(false);
        setSelectedQuote(null);
      }}
    >
      <View style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>
            {selectedQuote ? selectedQuote.id : 'Quote Details'}
          </Text>
          <TouchableOpacity 
            style={styles.modalCloseButton}
            onPress={() => {
              setShowQuoteModal(false);
              setSelectedQuote(null);
            }}
          >
            <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
          </TouchableOpacity>
        </View>
        
        {selectedQuote && (
          <ScrollView style={styles.modalContent}>
            <View style={styles.quotePreview}>
              <View style={styles.quotePreviewHeader}>
                <Text style={styles.quotePreviewTitle}>{selectedQuote.projectTitle}</Text>
                <View style={[styles.statusBadge, { backgroundColor: getStatusColor(selectedQuote.status) }]}>
                  <MaterialCommunityIcons 
                    name={getStatusIcon(selectedQuote.status)} 
                    size={12} 
                    color="#FFFFFF" 
                  />
                  <Text style={styles.statusText}>{selectedQuote.status}</Text>
                </View>
              </View>
              
              <View style={styles.quotePreviewSection}>
                <Text style={styles.quotePreviewSectionTitle}>Customer</Text>
                <Text style={styles.quotePreviewText}>
                  {selectedQuote.customerName}{'\n'}
                  {selectedQuote.customerCompany}{'\n'}
                  {selectedQuote.customerEmail}
                </Text>
              </View>
              
              <View style={styles.quotePreviewSection}>
                <Text style={styles.quotePreviewSectionTitle}>Quote Items</Text>
                {selectedQuote.items.map((item, index) => (
                  <View key={index} style={styles.quotePreviewItem}>
                    <Text style={styles.quotePreviewItemDescription}>{item.description}</Text>
                    <Text style={styles.quotePreviewItemDetails}>
                      {item.quantity} × {formatCurrency(item.unitPrice)} = {formatCurrency(item.total)}
                    </Text>
                  </View>
                ))}
              </View>
              
              <View style={styles.quotePreviewSection}>
                <Text style={styles.quotePreviewSectionTitle}>Summary</Text>
                <View style={styles.quoteSummary}>
                  <View style={styles.quoteSummaryRow}>
                    <Text style={styles.quoteSummaryLabel}>Subtotal:</Text>
                    <Text style={styles.quoteSummaryValue}>{formatCurrency(selectedQuote.subtotal)}</Text>
                  </View>
                  <View style={styles.quoteSummaryRow}>
                    <Text style={styles.quoteSummaryLabel}>Tax (20%):</Text>
                    <Text style={styles.quoteSummaryValue}>{formatCurrency(selectedQuote.tax)}</Text>
                  </View>
                  <View style={[styles.quoteSummaryRow, styles.quoteSummaryTotal]}>
                    <Text style={styles.quoteSummaryTotalLabel}>Total:</Text>
                    <Text style={styles.quoteSummaryTotalValue}>{formatCurrency(selectedQuote.total)}</Text>
                  </View>
                </View>
              </View>
              
              <View style={styles.quotePreviewSection}>
                <Text style={styles.quotePreviewSectionTitle}>Terms & Conditions</Text>
                <Text style={styles.quotePreviewText}>
                  Payment Terms: {selectedQuote.terms}{'\n'}
                  Valid Until: {formatDate(selectedQuote.validUntil)}{'\n'}
                  <Text>{selectedQuote.notes && `Notes: ${selectedQuote.notes}`}</Text>
                </Text>
              </View>
            </View>
          </ScrollView>
        )}
        
        <View style={styles.modalFooter}>
          <TouchableOpacity 
            style={[styles.modalButton, styles.exportButton]}
            onPress={() => Alert.alert('Export', 'Quote would be exported as PDF')}
          >
            <MaterialCommunityIcons name="download" size={16} color="#FFFFFF" />
            <Text style={styles.exportButtonText}>Export PDF</Text>
          </TouchableOpacity>
          {selectedQuote?.status === 'draft' && (
            <TouchableOpacity 
              style={[styles.modalButton, styles.sendButton]}
              onPress={() => {
                handleSendQuote(selectedQuote.id);
                setShowQuoteModal(false);
                setSelectedQuote(null);
              }}
            >
              <MaterialCommunityIcons name="send" size={16} color="#FFFFFF" />
              <Text style={styles.sendButtonText}>Send Quote</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    </Modal>
  );

  if (isLoading) {
    return (
      <SafeAreaView style={styles.loadingContainer}>
        <ActivityIndicator size={36} color="#3B82F6" />
        <Text style={styles.loadingText}>Loading Quote Generator...</Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#1F2937" />
      
      {renderHeader()}
      {renderTabs()}
      {renderContent()}
      {renderCreateModal()}
      {renderQuoteModal()}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
  },
  
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#6B7280',
    fontWeight: '500',
  },
  
  // Header styles
  header: {
    paddingTop: 12,
    paddingBottom: 16,
    paddingHorizontal: 16,
  },
  
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  headerCenter: {
    flex: 1,
    alignItems: 'center',
  },
  
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 2,
  },
  
  headerButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  // Tabs styles
  tabsContainer: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  
  tabsContent: {
    paddingHorizontal: 16,
  },
  
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginRight: 8,
    borderRadius: 20,
  },
  
  activeTab: {
    backgroundColor: '#EBF4FF',
  },
  
  tabText: {
    fontSize: 14,
    color: '#6B7280',
    marginLeft: 8,
    fontWeight: '500',
  },
  
  activeTabText: {
    color: '#3B82F6',
    fontWeight: '600',
  },
  
  // Content styles
  contentContainer: {
    flex: 1,
  },
  
  listContainer: {
    padding: 16,
  },
  
  // Search styles
  searchContainer: {
    padding: 16,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F3F4F6',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#1F2937',
    marginLeft: 8,
  },
  
  // Stats styles
  statsContainer: {
    padding: 16,
  },
  
  row: {
    justifyContent: 'space-between',
  },
  
  statCard: {
    width: (width - 48) / 2,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    alignItems: 'center',
  },
  
  statIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  
  statValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 4,
  },
  
  statTitle: {
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'center',
  },
  
  // Quote card styles
  quoteCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  quoteHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  
  quoteInfo: {
    flex: 1,
  },
  
  quoteId: {
    fontSize: 14,
    fontWeight: '600',
    color: '#3B82F6',
    marginBottom: 4,
  },
  
  quoteTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
  },
  
  quoteCustomer: {
    fontSize: 14,
    color: '#6B7280',
  },
  
  quoteStatus: {
    alignItems: 'flex-end',
  },
  
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  
  statusText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 4,
    textTransform: 'capitalize',
  },
  
  quoteDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  
  quoteDetail: {
    flex: 1,
  },
  
  quoteDetailLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 4,
  },
  
  quoteDetailValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  quoteActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    marginLeft: 8,
  },
  
  actionButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 4,
  },
  
  // Template card styles
  templateCard: {
    width: (width - 48) / 2,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  templateHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  
  templateName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    flex: 1,
  },
  
  templateCategory: {
    backgroundColor: '#EBF4FF',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  
  templateCategoryText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#3B82F6',
  },
  
  templateDescription: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 12,
  },
  
  templateDetails: {
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    paddingTop: 8,
  },
  
  templateDetail: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 2,
  },
  
  // Analytics styles
  analyticsSection: {
    backgroundColor: '#FFFFFF',
    margin: 16,
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 16,
  },
  
  statusDistribution: {
    flexDirection: 'column',
  },
  
  statusItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  
  statusIndicator: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 12,
  },
  
  statusLabel: {
    flex: 1,
    fontSize: 14,
    color: '#6B7280',
    textTransform: 'capitalize',
  },
  
  statusCount: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  // Modal styles
  modalContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
    flex: 1,
  },
  
  modalCloseButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#F3F4F6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  modalContent: {
    flex: 1,
    padding: 16,
  },
  
  modalFooter: {
    flexDirection: 'row',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  
  modalButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 8,
    marginHorizontal: 4,
  },
  
  cancelButton: {
    backgroundColor: '#F3F4F6',
  },
  
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#6B7280',
  },
  
  saveButton: {
    backgroundColor: '#3B82F6',
  },
  
  saveButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  
  exportButton: {
    backgroundColor: '#6B7280',
  },
  
  exportButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  
  sendButton: {
    backgroundColor: '#3B82F6',
  },
  
  sendButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  
  // Form styles
  formSection: {
    marginBottom: 24,
  },
  
  formSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 12,
  },
  
  formSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  
  formInput: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: '#1F2937',
    backgroundColor: '#FFFFFF',
    marginBottom: 12,
  },
  
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  
  addItemButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#3B82F6',
  },
  
  addItemText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#3B82F6',
    marginLeft: 4,
  },
  
  quoteItemForm: {
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
  },
  
  quoteItemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  
  quoteItemTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  removeItemButton: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#FEE2E2',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  quoteItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  
  quantityInput: {
    flex: 1,
    marginRight: 8,
  },
  
  priceInput: {
    flex: 2,
    marginRight: 8,
  },
  
  totalText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#10B981',
    minWidth: 80,
    textAlign: 'right',
  },
  
  // Quote preview styles
  quotePreview: {
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    padding: 16,
  },
  
  quotePreviewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  
  quotePreviewTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
    flex: 1,
  },
  
  quotePreviewSection: {
    marginBottom: 16,
  },
  
  quotePreviewSectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 8,
  },
  
  quotePreviewText: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
  },
  
  quotePreviewItem: {
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    padding: 12,
    marginBottom: 8,
  },
  
  quotePreviewItemDescription: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
  },
  
  quotePreviewItemDetails: {
    fontSize: 12,
    color: '#6B7280',
  },
  
  quoteSummary: {
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    padding: 12,
  },
  
  quoteSummaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  
  quoteSummaryLabel: {
    fontSize: 14,
    color: '#6B7280',
  },
  
  quoteSummaryValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  quoteSummaryTotal: {
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    paddingTop: 8,
    marginTop: 8,
    marginBottom: 0,
  },
  
  quoteSummaryTotalLabel: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1F2937',
  },
  
  quoteSummaryTotalValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#10B981',
  },
});
